import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "../ui/button";
// import { CharterSignatureCounter } from "./CharterSignatureCounter"; // Hidden for now
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import chartePreview from "@/assets/charte-preview.png";
import { Card, CardContent } from "../ui/card";
import { CharteSignatureForm } from "./CharteSignatureForm";
export const Vision = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchPdfUrl = async () => {
    try {
      setLoading(true);
      setError(null);
      const {
        data: files,
        error: filesError
      } = await supabase.storage.from('manifeste').list();
      if (filesError) {
        console.error('Error listing files in "manifeste" bucket:', filesError);
        throw new Error('Erreur lors de la vérification des fichiers');
      }
      console.log('Files in manifeste bucket:', files);
      const {
        data,
        error
      } = await supabase.storage.from('manifeste').createSignedUrl('ManifesteLEDLR.pdf', 3600);
      if (error) {
        console.error('Error creating signed URL:', error);
        throw error;
      }
      if (!data || !data.signedUrl) {
        throw new Error('Échec de génération de l\'URL signée');
      }
      console.log('PDF URL generated successfully');
      setPdfUrl(data.signedUrl);
    } catch (err) {
      console.error('Error fetching PDF:', err);
      setError('Impossible de charger le manifeste. Veuillez télécharger le fichier ManifesteLEDLR.pdf dans le bucket "manifeste".');
      toast.error("Erreur de chargement", {
        description: "Le document n'a pas pu être chargé. Veuillez télécharger le fichier ManifesteLEDLR.pdf dans le bucket manifeste."
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isDialogOpen && !pdfUrl) {
      fetchPdfUrl();
    }
  }, [isDialogOpen, pdfUrl]);
  return <section className="py-12 bg-white">
      <div className="container px-4 md:px-6">
        <div className="flex flex-col md:flex-row items-start justify-between gap-8">
          <div className="w-full md:w-3/5 space-y-4">
            <div className="relative inline-block mb-2">
              <h2 className="text-2xl font-bold font-acumin text-[#00123A] relative z-10 md:text-2xl">Pourquoi créer cette l'association ? Enfants de la République</h2>
              <div className="absolute bottom-0 left-0 w-full h-[40%] bg-[#39CCFF] -z-10"></div>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                Il est des promesses qui façonnent les peuples et des idéaux qui traversent le
                temps. La République française est de ceux-là car la France n'est pas une nation
                comme les autres : sa démocratie est aussi une République. Au fonctionnement de ses institutions et de ses lois formant un état de droit, s'ajoutent un corpus de valeurs, la faisant devenir un souffle, un projet de
                civilisation, une promesse faite à tous ses enfants d'une liberté inaliénable, d'une
                égalité intangible, d'une fraternité indéfectible et d'une laïcité garante d'un avenir
                collectif.
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                Mais cette promesse, si précieuse, cet horizon commun, vacille aujourd'hui,
                attaqué ouvertement par les extrémismes de tout bord. Contestée à l'extérieur
                par des forces qui rêvent d'en finir avec son modèle universaliste, minée à
                l'intérieur par des courants d'intérêts particuliers qui la jugent dépassée,
                discriminante ou oppressive, la République française se trouve affaiblie et en
                danger.
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                <strong>Un idéal à raviver</strong><br />
                Nous, Enfants de la République, comme d'autres citoyens, refusons de rester spectateurs. Nous sommes issus de tous horizons, professions et origines, unis par un seul engagement : faire vivre et défendre cet idéal républicain que nous avons reçu en héritage et que nous nous devons de transmettre à nos générations futures.
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                La République, loin d'être une structure figée, est un processus vivant. Elle se nourrit de l'engagement de ses citoyens, de leur attachement à ses valeurs, à ses lois, synthétisée dans la Charte des droits et des devoirs du citoyen français (article 21-24 du code civil – JO du 31 janvier 2012).
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                Dès lors, notre action repose sur trois piliers :
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-sm leading-relaxed text-gray-700">
                <li>
                  <strong>Transmettre :</strong> Nous voulons faire redécouvrir aux jeunes générations les valeurs et les réalisations de la République, leur donner les outils pour en être à leur tour les artisans et les gardiens.
                </li>
                <li>
                  <strong>Renforcer :</strong> Nous plaidons pour une extension de la Charte des droits et des devoirs du citoyen français à l'ensemble des citoyens ainsi qu'à une évolution de sa portée dans notre vie civique afin que chacun, né ou devenu Français, prenne conscience de ce que signifie appartenir à cette nation singulière.
                </li>
                <li>
                  <strong>Protéger :</strong> Nous sommes déterminés à mener des actions en justice contre ceux qui, par calcul ou par négligence, s'attaquent aux fondements de la République, qu'ils soient élus ou citoyens, institutions ou groupes d'influence.
                </li>
              </ol>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                <strong>Un engagement citoyen</strong><br />
                Profondément laïques, nous ne sommes ni un parti politique, ni une force d'opposition. Nous sommes des citoyens de toutes les conditions sociales et de toutes les composantes de la société française. 
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                Nous sommes une force de proposition pour un projet collectif contre la fragmentation de notre République car elle n'est pas un acquis : elle est une exigence. Une exigence de vérité, de courage et de transmission que, pour notre part, nous vivons comme une chance.
              </p>
              <p className="text-sm leading-relaxed text-gray-700 text-justify">
                Nous, Enfants de la République, avons choisi de relever ce défi et de ramasser le flambeau afin de le transmettre intact dans son esprit à nos propres enfants en rappelant qu'être citoyen ne consiste pas seulement à revendiquer des droits mais aussi à accepter des devoirs.
              </p>
              <p className="text-right italic text-gray-800 font-medium">
                - Manifeste des Enfants de la République
              </p>
              
              
            </div>
          </div>
          
          <div className="w-full md:w-2/5 mt-6 md:mt-0 flex flex-col items-center gap-6">
            {/* <CharterSignatureCounter /> */}
            <a 
              href="https://ledlr.org/Charte_des_droits_et_devoirs_du_citoyen_francais-juin-2020.pdf" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block hover:opacity-90 transition-opacity shadow-lg rounded-lg overflow-hidden"
            >
              <img 
                src={chartePreview} 
                alt="Charte des droits et devoirs du citoyen français" 
                className="max-w-full h-auto"
              />
            </a>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Card className="bg-[#FFFF4D] border-2 border-[#FFFF4D] shadow-lg cursor-pointer hover:bg-[#FFFF4D]/90 transition-colors w-full max-w-sm">
                  <CardContent className="p-5">
                    <h3 className="text-xl font-semibold mb-3 text-[#00123A]">Signez la charte</h3>
                    <p className="text-[#00123A] mb-4">Soutenez l'action des Enfants de la République en signant La Charte. Vous recevrez gratuitement votre charte "Enfants de la République" personnalisée et numérotée !</p>
                    <button className="w-full bg-secondary text-white px-4 py-2.5 rounded-lg hover:bg-secondary/90 transition-colors font-semibold">
                      Je signe la charte
                    </button>
                  </CardContent>
                </Card>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Signer la charte républicaine</DialogTitle>
                </DialogHeader>
                <CharteSignatureForm onSuccess={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>;
};